/**
 * useAutoBump — handles live run-long pushback for actively running tickets.
 * Converted-rollover pause-growth is written by the SERVER at every pause/resume boundary
 * (W4c: ticket_pause_sessions trigger, migration 20270206062100). These paths are complementary, not duplicate.
 *
 * DEPENDS ON: globalCascade never overwrites opti_finish for locked (running) tickets.
 * If that invariant changes, bump extensions will be silently reverted by cascade.
 * See Round 37, FIX 10.
 * MUTATES: tickets.start_time, tickets.opti_finish only (via reassign-ticket). Never writes predicted_minutes, accumulated_rollover_minutes, or rollover_type.
 */
import { resolvePlanningBufferPct } from '@/utils/planningBuffer';
import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { differenceInMinutes, addMinutes, parseISO, isAfter, isBefore, format } from 'date-fns';
import { toZonedTime, fromZonedTime } from 'date-fns-tz';
import { TIMER_NOT_STARTED_WARNING_MIN, DEBUG_AUTO_BUMP } from '@/constants/schedulingConstants';
import { getTodayInShopTz, getTechDayHours, getTechShiftBoundaryUtc, getShopDayLabel } from '@/utils/shopTime';
import { calculateWallMinutes, calculateTicketOptiFinish, parseTimeToMinutes } from '@/utils/canonicalTimeMath';
import { getMinutesInShopTZ } from '@/utils/timezoneMath';
import { triggerBoardCascade } from '@/utils/cascadeTrigger';
import { isChainGovernedRow, replanRolloverChainFromRow } from '@/utils/rolloverChain';
import { requestConversionMaintenance } from '@/lib/scheduling/rolloverConversionClient';
import { pushClientError } from '@/utils/clientErrorBuffer';
// Core-100 R4 (CORE-AC): every finish this hook used to compute and write is now DERIVED BY THE SERVER
// (`ticket-finish-intent`); the hook states the intent. The table refuses a browser-written finish.
import { requestServerFinish } from '@/utils/serverFinishIntent';
import { getScheduleFreshness, isScheduleFresh } from '@/lib/scheduling/serverEodState';
// W4b: every live ticket of a (multi-bay) technician gets its finish maintained, not only the queue's first.
import { liveTicketFinishIntent } from '@/lib/scheduling/liveTicketCoverage';
// DTC-B: route every "current instant" read through the canonical
// clock so auto-bump tick decisions, throttle anchors, and the
// lastProcessedRef seed are deterministic and test-freezable. The
// `new Date(<existing>)` parsing sites at L192 (sortedTickets sort)
// and L969 (ceilToNextMinute copy-construct) are intentionally NOT
// routed through the clock — they parse stored timestamps / clone an
// existing Date arg, not "now".
import { clock } from '@/utils/clock';
// FLOYD-8: the ONE chain-ownership rule, shared with TechnicianColumn and
// mirrored for Deno in _shared/chainOwnership.ts.
import { effectiveChainOwnerId } from '@/lib/scheduling/chainOwnership';
// VIS-A1: route the recurring 60s tick through `createStaggeredInterval`
// so this hook anchors the canonical phase-0 slot of the polling trio.
// AutoBump's offset is 0 (i.e. setInterval anchors to mount-time, identical
// to pre-VIS-A1 behavior); the change is structural — making the phase
// relationship to its sibling 60s hooks (useOptiFinishPushback,
// useConvertedRolloverGrowth) explicit so a future hook can pick a non-
// overlapping slot or so a regression (two siblings collapsing to the
// same offset) is obvious from a single grep on `VIS_A1_STAGGER`.
import { createStaggeredInterval, VIS_A1_STAGGER } from '@/utils/staggeredInterval';
import { notifyDayboardInvalidated } from '@/utils/dayboardInvalidation';
// R3-A OPTION B (founder decision 2026-10-21): Run Long AutoBump runs on the SERVER (Edge function
// run-long-autobump, every minute from the one pg_cron job, with no browser open). Each tick asks the server to
// reconcile first; when it answers, this hook runs NOTICE-ONLY (`serverOwnsWrites` — every write and cascade below
// is skipped by that one flag). On an error / an older server it falls back to its own requests unchanged.
import { requestRunLongReconcile, serverOwnsRunLong } from '@/lib/scheduling/runLongReconcileClient';
// Virgin EOD boundary. A never-started ticket that cannot finish today is a
// SCHEDULING conflict, not a rollover — and raising it must not stop the
// canonical no-past-time bump. See the module header for the doctrine.
import {
  classifyEodConflict,
  STARTED_WORK_EOD_EXCEEDED_EVENT,
  VIRGIN_EOD_EXCEEDED_EVENT,
  type VirginEodSchedulingConflictDetail,
} from '@/utils/virginEodSchedulingConflict';

// FIX-R132-HIGH-1: throttle the noisy "auto-bump update failed" toast so a
// persistent failure mode (e.g. RLS regression on tickets, network blip)
// doesn't spam the operator every 15s while still surfacing the first
// occurrence and any later recurrences after a quiet period. The buffer
// push always fires so support gets every failure attached to the next
// bug report.
const AUTO_BUMP_TOAST_THROTTLE_MS = 60_000;
let lastAutoBumpToastAt = 0;

function reportAutoBumpFailure(params: {
  phase: 'running' | 'paused' | 'rollover_running' | 'rollover_paused';
  ticketId: string;
  shopId: string | null;
  err: unknown;
}) {
  const errMsg =
    (params.err as { message?: string } | undefined)?.message
    ?? (typeof params.err === 'string' ? params.err : JSON.stringify(params.err ?? {}));
  console.error(`[AutoBump] ${params.phase} update failed for ticket ${params.ticketId}:`, params.err);
  pushClientError({
    source: 'mutation',
    message: `[AutoBump] ${params.phase} update failed: ${errMsg}`,
    extra: { ticketId: params.ticketId, phase: params.phase },
    shopId: params.shopId,
  });
  const now = clock.nowMs();
  if (now - lastAutoBumpToastAt >= AUTO_BUMP_TOAST_THROTTLE_MS) {
    lastAutoBumpToastAt = now;
    toast({
      title: '⚠️ Schedule update failed',
      description: 'The auto-bump tick could not save changes. Refresh the board if positions look stale.',
      variant: 'destructive',
      duration: 6000,
    });
  }
}

interface AutoBumpConfig {
  enabled: boolean;
  selectedDayLabel: string;  // shop-canonical YYYY-MM-DD
  shopTimezone: string;      // from useShopTimezone() in caller
  shopId: string | null;     // H-1/H-2: Added to scope shop_profile and technician queries
  onTicketUpdate?: () => void;
}

/**
 * Auto-bump system for managing ticket start times
 * 
 * Rules:
 * 1. First ticket (top of queue) with NO running timer: bumps to current time every minute
 * 2. First ticket with RUNNING timer: locked, no bumps
 * 3. Auto-bump ONLY changes start_time and optifinish - NEVER repositions tickets
 * 4. Scheduled tickets (Waiters) are LOCKED - cannot be bumped off scheduled time
 * 5. Cannot bump into breaks (jumps to after break)
 * 6. Optifinish cannot overlap with next scheduled ticket's start time
 * 7. Ticket completion triggers full schedule recalculation (handled separately)
 * 
 * Note: Timer-not-started warning (TIMER_NOT_STARTED_WARNING_MIN) is implemented
 * in the body below. Rollover/Due-Out risk checks live in eodRolloverHandler.ts.
 */
export const useAutoBump = ({ enabled, selectedDayLabel, shopTimezone, shopId, onTicketUpdate }: AutoBumpConfig) => {
  // VIS-A1: ref now holds the cleanup function returned by
  // createStaggeredInterval (which manages BOTH the deferred startup timer
  // and the eventual setInterval handle) instead of a raw NodeJS.Timeout.
  // Disposal at the early-return branch and the effect cleanup is now a
  // single `intervalRef.current?.()` call regardless of whether the startup
  // delay has fired yet.
  const intervalRef = useRef<(() => void) | null>(null);
  const lastProcessedRef = useRef<Date>(clock.now());
  const warnedScheduledRef = useRef<Set<string>>(new Set());
  const processingRef = useRef(false);
  const lastRunAtRef = useRef<number>(0);
  const ticketCooldownRef = useRef<Record<string, number>>({});
  const timerNotStartedWarningRef = useRef<Set<string>>(new Set()); // NEW: Track warned tickets
  // Core-100 R4 CORE-BO: present vehicles already suggested this session (the opening itself is deduplicated by the server).
  const presentVehicleSuggestedRef = useRef<Set<string>>(new Set());
  // Virgin EOD scheduling conflict — raised once per ticket per session.
  // Same proven shape as `warnedScheduledRef` / `timerNotStartedWarningRef`
  // above. It gates the NOTICE only; the canonical bump is never gated by it,
  // so a dismissal cannot re-freeze Start Time in the past.
  const virginEodWarnedRef = useRef<Set<string>>(new Set());


  useEffect(() => {
    if (!enabled) {
      // VIS-A1: dispose any prior staggered-interval cleanup. Also clears
      // a still-pending startup setTimeout if enabled flips false within
      // the first 60s after mount.
      intervalRef.current?.();
      intervalRef.current = null;
      return;
    }

    // [AutoBump-DEBUG] === DIAGNOSTIC ===
    // Gated behind DEBUG_AUTO_BUMP, the same constant M-3 introduced to
    // replace the inline `const DEBUG = true` flags elsewhere in this file.
    // These three diagnostics were added later and bypassed it, so they
    // shipped unconditionally: this one fires once per effect re-run, and the
    // deps settle several times during Dashboard boot (~8 lines per load in
    // production). The traces are retained verbatim — flip DEBUG_AUTO_BUMP in
    // schedulingConstants.ts to bring the whole auto-bump trace back at once.
    if (DEBUG_AUTO_BUMP) console.log('[AutoBump-DEBUG] hook mount/enabled', {
      enabled,
      selectedDayLabel,
      shopTimezone,
      shopId,
      todayInShopTz: getTodayInShopTz(shopTimezone),
      match: selectedDayLabel === getTodayInShopTz(shopTimezone),
    });
    // [AutoBump-DEBUG] === END DIAGNOSTIC ===

    const processAutoBump = async () => {
      if (processingRef.current) return;
      // V-E-2 R151-MED-2: skip when the tab is hidden. processAutoBump fires
      // a heavy chain (shop_profile + technicians + tickets fetch + cascade
      // trigger) every 60s; running it on background tabs wastes DB hits at
      // multi-tenant scale. The next visibilitychange will resume processing
      // because the interval continues to fire — we just early-return until
      // the tab is visible again.
      if (typeof document !== 'undefined' && document.hidden) return;
      const nowTs = clock.nowMs();
      if (nowTs - lastRunAtRef.current < 15000) return; // throttle to max once per 15s
      processingRef.current = true;

      try {
        const now = clock.now();
        const dateStr = selectedDayLabel;
        const todayStr = getTodayInShopTz(shopTimezone);

        // [AutoBump-DEBUG] === DIAGNOSTIC ===
        if (DEBUG_AUTO_BUMP) console.log('[AutoBump-DEBUG] tick start', {
          shopId,
          dateStr,
          todayStr,
          tabHidden: typeof document !== 'undefined' && document.hidden,
        });
        // [AutoBump-DEBUG] === END DIAGNOSTIC ===

        // Only process for today's tickets (shop-local comparison)
        if (dateStr !== todayStr) return;

        // H-1: Guard — no shopId means we can't scope queries correctly
        if (!shopId) return;

        // R3-A Option B: the server reconciles this shop's run-long timing (finish intents, chain growth, the lane
        // walk) through CORE-AC. When it answered, the writes below are its, not this tab's: NOTICE-ONLY.
        const serverRun = await requestRunLongReconcile(shopId);
        const serverOwnsWrites = serverOwnsRunLong(serverRun);
        if (serverOwnsWrites && (serverRun.applied ?? 0) > 0) onTicketUpdate?.();

        // Fetch shop profile for breaks, EOD settings, and timezone
        // H-1: Scope to current shop — prevents fetching wrong shop's breaks/settings
        const { data: shopProfile } = await supabase
          .from('shop_profile')
          .select('breaks, shop_culture')  // time_zone removed — passed via shopTimezone prop
          .eq('shop_id', shopId)
          .maybeSingle();

        const breaks = (shopProfile?.breaks as any[]) || [];
        const shopCulture = (shopProfile?.shop_culture as any) || {};
        // shopTimezone is now passed in via props — no need to fetch it here
        const willingnessRollPastEOD = shopCulture.willingness_roll_past_eod || 0;

        // Fetch all active technicians
        // H-2: Scope to current shop — previously fetched ALL shops' active technicians
        const { data: technicians } = await supabase
          .from('technicians')
          .select('id, name, shift_start, shift_end, hours, efficiency_percentage, buffer_percentage, recurring_breaks, non_recurring_breaks')
          .eq('employment_status', 'Active')
          .eq('shop_id', shopId);

        if (!technicians) return;

        // Fetch today's tickets grouped by technician
        // HIGH 2.6 fix: Use canonical .eq('scheduled_date', todayStr) instead of naive UTC range.
        // R125-H-3: narrow select — AutoBump reads id/type/customer_name/
        // start_time/due_out/predicted_minutes/actual_start_time/
        // actually_started/timer_status/status/rollover_of_ticket_id/
        // parent_ticket_id/scheduled_date/assigned_tech_id/shop_id/
        // parking_lot/bay_id/opti_finish. select('*') previously pulled
        // customer_phone/customer_email into React Query cache on every
        // AutoBump tick (runs frequently on the board).
        //
        // The following columns are intentionally NOT in the SELECT
        // because the deployed core-prod `public.tickets` schema does
        // not have them (PostgREST returns 42703 — column does not
        // exist — on any query that includes them):
        //
        //   * `original_ticket_id` — read by `isRolloverChain` below
        //     through a hasOwnProperty guard; undefined-due-to-missing-
        //     column resolves to null and does not taint chain detection.
        //   * `completed_minutes` — never read in this hook (pure dead-
        //     select cleanup; no fallback needed).
        //   * `run_long_opti_baseline` — never read here (the server
        //     freezes it, Core-100 R4).
        //   * R3-A Option B: `run_long_minutes` IS selected now. It exists
        //     (20251223010856, ADD COLUMN ... DEFAULT 0; the server's
        //     ticket-finish-intent reads it). Without it `currentRunLong`
        //     below was always 0, so the has-rollover branch re-added the
        //     WHOLE run-long to the chain on every tick.
        //   * `original_start_time`, `accumulated_bump_minutes` — bump-
        //     tracking metadata. Both columns DO exist on core-prod
        //     (migrations 20251128214605 and 20251104052207 added them
        //     with `IF NOT EXISTS`). Re-included in the SELECT so the
        //     hasOwnProperty guards on the write side (below) evaluate
        //     true, the companion field writes fire, and the TicketCard
        //     bump indicator at TicketCard.tsx:2186-2222 has data to
        //     render. The guards stay defensive — if the columns ever
        //     stop existing on a future redeploy, the writes silently
        //     skip rather than 42703.
        //
        // CODEX_DEVIATION: Client-side filter is intentionally stricter than
        // globalCascade.ts:147-168 (server). Server admits ALL Active+Paused
        // tickets to compute authoritative scheduling slots. Client limits
        // Paused to actually_started=true + bay_id IS NOT NULL, focusing on
        // physically-started bay occupants only — prevents UI jitter from
        // unpositioned Paused tickets. DO NOT unify these filters without
        // updating both sides in tandem.
        //
        // Query split rationale: PostgREST returns HTTP 400 on
        //   .or('status.eq.Active,and(status.eq.Paused,actually_started.eq.true,bay_id.not.is.null)')
        // because its nested-boolean parser doesn't accept the `.not.is.null`
        // prefix two levels deep (inside and(...) inside or=(...)). The
        // single `.or(...)` is replaced with two `.eq('status', ...)` queries
        // running in parallel and merged client-side. The intended filter is
        // preserved verbatim: Active OR (Paused AND actually_started AND
        // bay_id IS NOT NULL). The two queries cannot return overlapping rows
        // because Active and Paused are disjoint status values.
        const TICKETS_SELECT =
          'id, type, customer_name, start_time, due_out, predicted_minutes, actual_start_time, actually_started, timer_status, status, rollover_of_ticket_id, parent_ticket_id, scheduled_date, assigned_tech_id, scheduled_tech_id, shop_id, parking_lot, bay_id, opti_finish, original_start_time, accumulated_bump_minutes, rollover_conversion_id, run_long_minutes';

        const [activeRes, pausedRes] = await Promise.all([
          supabase
            .from('tickets')
            .select(TICKETS_SELECT)
            .eq('scheduled_date', todayStr)
            .eq('shop_id', shopId)
            .eq('status', 'Active'),
          supabase
            .from('tickets')
            .select(TICKETS_SELECT)
            .eq('scheduled_date', todayStr)
            .eq('shop_id', shopId)
            .eq('status', 'Paused')
            .eq('actually_started', true)
            .not('bay_id', 'is', null),
        ]);

        // [AutoBump-DEBUG] === DIAGNOSTIC ===
        if (activeRes.error || pausedRes.error) {
          console.warn('[AutoBump-DEBUG] tickets query error', {
            activeError: activeRes.error,
            pausedError: pausedRes.error,
          });
        }
        if (DEBUG_AUTO_BUMP) console.log('[AutoBump-DEBUG] tickets fetched', {
          active: activeRes.data?.length ?? 0,
          pausedStartedWithBay: pausedRes.data?.length ?? 0,
        });
        // [AutoBump-DEBUG] === END DIAGNOSTIC ===

        const tickets = [
          ...((activeRes.data as any[]) ?? []),
          ...((pausedRes.data as any[]) ?? []),
        ].sort(
          (a, b) =>
            new Date(a.start_time).getTime() - new Date(b.start_time).getTime(),
        );

        if (!tickets || tickets.length === 0) return;

        // ============================================================
        // CANONICAL CHAIN RULE — per-technician chain bucketing.
        //
        // The chain bucket is `assigned_tech_id` ALONE (not tech+bay).
        // Rationale: a technician's queue is a SINGLE time-ordered
        // sequence regardless of which bay each ticket is in — they
        // can only physically work one ticket at a time. Bay conflicts
        // and bay-compatibility are enforced separately (cascade's
        // bayOccupancyMap and bayGatingValidator). For AutoBump's
        // "only bump the first ticket" decision, per-tech is correct.
        //
        // Per the canonical rule, this hook directly mutates ONLY the
        // first ticket in each tech's chain. The behavior is:
        //
        //   1. Sort the tech's tickets by start_time ascending.
        //   2. Take sortedTickets[0] as the chain anchor.
        //   3. If the anchor is running/paused/scheduled-Waiter, its
        //      own branch handles it (extend opti_finish, dispatch
        //      waiter warning, etc.) and triggers cascade.
        //   4. If the anchor is a not-started Active ticket whose
        //      start_time is in the past, the not-started branch
        //      writes start_time + opti_finish atomically and
        //      triggers cascade.
        //   5. Cascade (globalCascade.ts:475) chains downstream
        //      tickets via `next.start_time = cursor + 1 min`, where
        //      cursor advances to each placed ticket's opti_finish.
        //
        // Downstream tickets are NEVER directly bumped by this hook —
        // they are only updated by the cascade triggered after the
        // anchor's mutation. This is the single guarantee that keeps
        // the canonical chain rule intact.
        // ============================================================
        //
        // FLOYD-8 — THE BUCKET KEY IS THE EFFECTIVE OWNER, NOT THE ASSIGNMENT.
        //
        // This opened with `if (!ticket.assigned_tech_id) return;`, which is
        // correct only while an assignment is the only way to own a ticket.
        // `tickets_parking_lot_immunity` forbids `parking_lot = true` with an
        // assignment set, so parking a scheduled ticket CLEARS
        // `assigned_tech_id` and leaves `scheduled_tech_id` carrying the plan.
        // Every such row — visible on the Dayboard as "Parked — scheduled for
        // <tech>" — was dropped here before the chain was built, so its Start
        // Time sat in the past and nothing advanced it.
        //
        // `effectiveChainOwnerId` is the same rule `TechnicianColumn` and
        // `localCascade` already applied, asked once instead of written four
        // times. It answers ownership ONLY; the status and virginity gates
        // below still decide whether the anchor may move, so Held-in-Parking
        // and devirginized tickets are untouched by this.
        const ticketsByTech: Record<string, any[]> = {};
        tickets.forEach(ticket => {
          const ownerId = effectiveChainOwnerId(ticket);
          if (!ownerId) return;
          if (!ticketsByTech[ownerId]) {
            ticketsByTech[ownerId] = [];
          }
          ticketsByTech[ownerId].push(ticket);
        });

        // [AutoBump-DEBUG] === DIAGNOSTIC ===
        console.log('[AutoBump-DEBUG] candidate tickets', {
          total: tickets?.length ?? 0,
          byTech: Object.fromEntries(
            Object.entries(ticketsByTech).map(([k, v]) => [k, (v as any[]).length]),
          ),
        });
        // [AutoBump-DEBUG] === END DIAGNOSTIC ===

        // Process each technician's tickets
        for (const [techId, techTickets] of Object.entries(ticketsByTech)) {
         try {
          const tech = technicians.find(t => t.id === techId);
          if (!tech) continue;

          // Sort tickets by start time
          const sortedTickets = techTickets.sort((a, b) =>
            new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
          );

          // ONLY process the first ticket in the queue (canonical chain rule —
          // see comment block above the ticketsByTech grouping).
          if (sortedTickets.length === 0) continue;

          const firstTicket = sortedTickets[0];

          // W4b (W5 interactions): a multi-bay technician has several live vehicles — the one they are hands-on
          // with, the ones they switched away from, the ones with a machine process running. Only the first in
          // start order used to be maintained; the others kept a stale finish and the lane under-reserved them.
          // Ask the server for each uncovered live ticket (it derives on the database row; switched-away and
          // unattended time is pause, never labor). The anchor's own running/paused branches below are unchanged.
          if (!serverOwnsWrites) {
            let liveApplied = false;
            for (let i = 0; i < sortedTickets.length; i++) {
              const live = sortedTickets[i];
              const intent = liveTicketFinishIntent(live, i === 0);
              if (!intent) continue;
              const r = await requestServerFinish(live.id, intent, {
                reason: `auto_bump_live_${intent}`,
                params: intent === 'run_long' ? { setBaseline: true } : { when: 'pause_grew' },
              });
              if (r.success && r.outcome === 'APPLIED') liveApplied = true;
            }
            if (liveApplied) {
              await triggerBoardCascade(shopId!, todayStr, techId);
              onTicketUpdate?.();
            }
          }

          // [AutoBump-DEBUG] === DIAGNOSTIC: chain decision ===
          // Surface the full chain ordering for this tech so the canonical
          // chain rule is observable at runtime. Downstream tickets MUST be
          // updated only by cascade — this log makes it obvious which ticket
          // AutoBump will directly touch and which are awaiting cascade.
          console.log('[AutoBump-DEBUG] tech chain', {
            techId,
            techName: tech.name,
            chainLength: sortedTickets.length,
            chainTicketIds: sortedTickets.map(t => t.id),
            directBumpCandidate: firstTicket.id,
            downstreamCount: sortedTickets.length - 1,
            note: 'AutoBump directly mutates only directBumpCandidate; downstream tickets converge via cascade',
          });
          // [AutoBump-DEBUG] === END DIAGNOSTIC ===
          const startTime = parseISO(firstTicket.start_time);
          const isScheduled = firstTicket.type === 'Waiter'; // Waiter tickets are locked to scheduled time

          // [AutoBump-DEBUG] === DIAGNOSTIC ===
          console.log('[AutoBump-DEBUG] tech first-ticket', {
            techId,
            techName: tech.name,
            ticketId: firstTicket.id,
            type: firstTicket.type,
            status: firstTicket.status,
            startTime: firstTicket.start_time,
            startTimePast: isBefore(startTime, now),
            actually_started: firstTicket.actually_started,
            timer_status: firstTicket.timer_status,
            parking_lot: firstTicket.parking_lot,
            bay_id: firstTicket.bay_id,
            parent_ticket_id: firstTicket.parent_ticket_id,
            rollover_of_ticket_id: firstTicket.rollover_of_ticket_id,
          });
          // [AutoBump-DEBUG] === END DIAGNOSTIC ===

          // NEW PHASE 2: Check for timer not started warning (+15 min past start)
          const minutesPastStart = differenceInMinutes(now, startTime);
          if (!firstTicket.actually_started && 
              minutesPastStart >= TIMER_NOT_STARTED_WARNING_MIN &&
              !timerNotStartedWarningRef.current.has(firstTicket.id)) {
            
            // Check if ticket has Due Out or would cause rollover
             const techHours = { ...tech, hours: tech.hours as Record<string, { open: string; close: string }> | null };
             const dayHoursWarning = getTechDayHours(techHours, todayStr);
             const shiftEnd = getTechShiftBoundaryUtc(todayStr, dayHoursWarning?.close || tech.shift_end, shopTimezone);
             const efficiency = tech.efficiency_percentage || 100;
            const buffer = resolvePlanningBufferPct(tech.buffer_percentage);
            const optiTime = calculateWallMinutes(firstTicket.predicted_minutes, efficiency, buffer);
            const projectedFinish = addMinutes(startTime, optiTime);
            const maxAllowedEnd = addMinutes(shiftEnd, willingnessRollPastEOD);
            
            const wouldRollover = isAfter(projectedFinish, maxAllowedEnd);
            const hasDueOut = firstTicket.due_out !== null;
            
            let warningMessage = `Timer not started for ${firstTicket.customer_name || 'ticket'} (+${minutesPastStart} min late)`;
            
            if (wouldRollover) {
              warningMessage += ` ⚠️ Will cause EOD rollover`;
            } else if (hasDueOut) {
              const dueOut = parseISO(firstTicket.due_out);
              const minutesUntilDue = differenceInMinutes(dueOut, now);
              if (minutesUntilDue < optiTime) {
                warningMessage += ` ⚠️ At risk of missing Due Out`;
              }
            }
            
            toast({
              title: '⏱️ Timer Not Started',
              description: warningMessage,
              variant: 'destructive',
              duration: 10000, // 10 second display
            });
            
            timerNotStartedWarningRef.current.add(firstTicket.id);
          }

          // CRITICAL: Check for running/paused timers
          // - RUNNING past OptiTime: update run_long_minutes (active overtime)
          // - PAUSED: update accumulated_bump_minutes (wait/pause time)
          if (firstTicket.timer_status === 'running' || firstTicket.timer_status === 'paused') {
            // R3-A Option B: the server wrote this lane's run-long / pause timing (and grew its chain).
            if (serverOwnsWrites) continue;

            const efficiency = tech.efficiency_percentage || 100;
            const buffer = resolvePlanningBufferPct(tech.buffer_percentage);
            const optiTime = calculateWallMinutes(firstTicket.predicted_minutes, efficiency, buffer);
            
            // Get worked minutes from timer sessions (completed sessions)
            const { data: sessions } = await supabase
              .from('ticket_timer_sessions')
              .select('duration_min, started_at, stopped_at')
              .eq('ticket_id', firstTicket.id);
            
            // Sum completed sessions + add current active session time
            let workedMinutes = 0;
            for (const s of (sessions || [])) {
              if (s.stopped_at) {
                // Completed session - use stored duration
                workedMinutes += s.duration_min || 0;
              } else if (s.started_at) {
                // Active session - calculate live elapsed time
                const sessionStart = parseISO(s.started_at);
                workedMinutes += differenceInMinutes(now, sessionStart);
              }
            }
            
            // Check if this ticket IS a rollover ticket (has a parent via canonical fields)
            const isRolloverTicket = !!firstTicket.rollover_of_ticket_id || !!firstTicket.parent_ticket_id;

            // Check if this ticket HAS a rollover child (canonical field lookup)
            // FIX-3 (Round 50): Walk the full chain (not just direct child) to catch
            // grandchildren in chains of depth ≥ 3. Capped at 10 hops.
            const rolloverTickets: any[] = [];
            {
              let currentChainId = firstTicket.id;
              for (let hop = 0; hop < 10; hop++) {
                const { data: nextDesc } = await supabase
                  .from('tickets')
                  .select('id, predicted_minutes, start_time, scheduled_date, parent_ticket_id, rollover_of_ticket_id')
                  .eq('rollover_of_ticket_id', currentChainId)
                  // DMC-D3b: canonical active-ticket filter — both reps
                  // excluded on this chain-walk lookup. Status IN
                  // excludes terminal states; deleted_at null is the
                  // defense-in-depth ghost-row guard.
                  .not('status', 'in', '("Completed","Cancelled","Deleted")')
                  .is('deleted_at', null)
                  .maybeSingle();
                if (!nextDesc) break;
                rolloverTickets.push(nextDesc);
                currentChainId = nextDesc.id;
              }
            }

            // Use the last descendant (tail) for growth propagation
            const rolloverTicket = rolloverTickets.length > 0 ? rolloverTickets[rolloverTickets.length - 1] : null;
            const hasRollover = !!rolloverTicket;

            // If THIS ticket is the rollover ticket, find the original (parent) ticket
            let parentTicket: { id: string; predicted_minutes: number } | null = null;
            if (isRolloverTicket && firstTicket.rollover_of_ticket_id) {
              const { data: parentData } = await supabase
                .from('tickets')
                .select('id, predicted_minutes, opti_finish, timer_status, status')
                .eq('id', firstTicket.rollover_of_ticket_id)
                .maybeSingle();
              parentTicket = parentData;
            }
            
            // Get tech's shift end using per-day hours and calculate EOD threshold
            const techHoursEod = { ...tech, hours: tech.hours as Record<string, { open: string; close: string }> | null };
            const dayHoursEod = getTechDayHours(techHoursEod, todayStr);
            const shiftEnd = getTechShiftBoundaryUtc(todayStr, dayHoursEod?.close || tech.shift_end, shopTimezone);
            const maxAllowedEnd = addMinutes(shiftEnd, willingnessRollPastEOD);
            
            if (firstTicket.timer_status === 'running') {
              // RUNNING: Check if timer has run past OptiTime
              // run_long_minutes = workedMinutes - optiTime (if positive)
              const runLongMinutes = Math.max(0, Math.ceil(workedMinutes - optiTime));
              const currentRunLong = firstTicket.run_long_minutes || 0;
              
              if (runLongMinutes > currentRunLong) {
                const additionalMinutes = runLongMinutes - currentRunLong;
                
                if (isRolloverTicket && parentTicket) {
                  // THIS TICKET IS THE ROLLOVER - extend THIS ticket's optifinish
                  // CSI-I2-A1 → Core-100 R4 (CORE-AC): the rollover-descendant self-extend is the
                  // server's 'run_long' derivation (finish = now + 5, run_long_minutes from the
                  // measured sessions). The bespoke `triggerBoardCascade` below still follows.
                  // The chain's future is the rollover chain authority's (addFutureWorkToRolloverChain), not here.
                  const mutateResult = await requestServerFinish(firstTicket.id, 'run_long', {
                    reason: 'auto_bump_rollover_running_self_extend',
                  });
                  if (mutateResult.refused) continue; // the server re-decided on the current row
                  const updateError = mutateResult.success ? null : { message: mutateResult.error };

                  if (!updateError) {
                    console.log(`[AutoBump] Rollover ticket ${firstTicket.id} running long: +${runLongMinutes} min (parent: ${parentTicket.id})`);

                    await triggerBoardCascade(shopId!, todayStr, techId);

                    toast({
                      title: '⏱️ Timer Running Long',
                      description: `${firstTicket.customer_name} (rollover) running +${runLongMinutes} min past OptiTime`,
                      duration: 5000,
                    });

                    onTicketUpdate?.();
                  } else {
                    // FIX-R132-HIGH-1
                    reportAutoBumpFailure({ phase: 'rollover_running', ticketId: firstTicket.id, shopId, err: updateError });
                  }
                } else if (hasRollover && rolloverTicket) {
                  // HAS ROLLOVER: Extend rollover ticket's predicted_minutes AND recalc its opti_finish
                  // Lock today's ticket at tech's shift end (strict EOD)

                  // M-1: Use scheduled_date to get the rollover ticket's shop day.
                  // new Date(...).toISOString().split('T')[0] returns UTC date which is wrong
                  // for shops west of UTC — rollover ticket may still be on the prior shop day.
                  const rolloverDateStr = (rolloverTicket as any).scheduled_date
                    ?? (rolloverTicket.start_time
                      ? getShopDayLabel(rolloverTicket.start_time, shopTimezone)
                      : todayStr);
                  
                  // The run-long minutes today's ticket cannot do today are MORE
                  // FUTURE WORK for its chain. oct20d (PR #13 round 4, item A): that growth is the
                  // SERVER's alone — claimed into the durable ledger (run_long_chain_growth) by a
                  // compare-and-swap of run_long_minutes and grown exactly once. This fallback (the
                  // server answered FAILED / not at all) neither grows the chain nor moves that
                  // watermark: it locks today's finish at the end of day (no track), so the board
                  // shows the lock, and the server claims EVERY minute not yet claimed when it runs
                  // again — nothing doubled (no second writer), nothing lost (the watermark moves
                  // only together with its ledger row).
                  const rolloverUpdateError: any = null;
                  if ((firstTicket as any).rollover_conversion_id) {
                    // W4: approved conversion — the server allocation sizes the continuation (re-derived from the
                    // measured sessions; idempotent, so asking from here never doubles it).
                    void requestConversionMaintenance((firstTicket as any).rollover_conversion_id);
                  }

                  // CSI-I2-A1 → Core-100 R4 (CORE-AC): today's-ticket EOD-lock is the server's
                  // 'eod_lock' derivation (finish = the technician's dated end of day, never before the
                  // start). oct20d: no track — run_long_minutes is the growth ledger's watermark.
                  const mutateResult = await requestServerFinish(firstTicket.id, 'eod_lock', {
                    reason: 'auto_bump_running_eod_lock_with_rollover',
                  });
                  if (mutateResult.refused || mutateResult.outcome === 'UNCHANGED') continue;
                  const updateError = mutateResult.success ? null : { message: mutateResult.error };

                  if (!updateError && !rolloverUpdateError) {
                    await triggerBoardCascade(shopId!, todayStr, techId);

                    console.log(`[AutoBump] Running ticket ${firstTicket.id} locked at EOD ${tech.shift_end}; ${additionalMinutes} min for rollover ${rolloverTicket.id} (${rolloverDateStr}) left to the server growth ledger`);
                    onTicketUpdate?.();
                  } else {
                    // FIX-R132-HIGH-1
                    reportAutoBumpFailure({
                      phase: 'running',
                      ticketId: firstTicket.id,
                      shopId,
                      err: updateError ?? rolloverUpdateError,
                    });
                  }
                } else {
                  // NO ROLLOVER: Normal behavior - extend today's ticket
                  // M-3: Use shared constant instead of inline const DEBUG = true
                  if (DEBUG_AUTO_BUMP) console.log(`[AutoBump] Running ticket ${firstTicket.id}: run_long ${currentRunLong} → ${runLongMinutes} min`);

                  // V-A-6 → Core-100 R4 (CORE-AC): the server's 'run_long' derivation writes the
                  // finish (now + 5) with run_long_minutes, and freezes run_long_opti_baseline the
                  // FIRST time the ticket runs long (it reads the stored baseline; this hook never
                  // selected it, so it used to rewrite the "frozen" value on every tick).
                  // The bespoke triggerBoardCascade below still follows.
                  const mutateResult = await requestServerFinish(firstTicket.id, 'run_long', {
                    reason: 'auto_bump_running_norollover',
                    params: { setBaseline: true },
                  });
                  if (mutateResult.refused) continue;
                  const updateError = mutateResult.success ? null : { message: mutateResult.error };

                  if (!updateError) {
                    await triggerBoardCascade(shopId!, todayStr, techId);

                    toast({
                      title: '⏱️ Timer Running Long',
                      description: `${firstTicket.customer_name} running +${runLongMinutes} min past OptiTime`,
                      duration: 5000,
                    });

                    onTicketUpdate?.();
                  } else {
                    // FIX-R132-HIGH-1
                    reportAutoBumpFailure({ phase: 'running', ticketId: firstTicket.id, shopId, err: updateError });
                  }
                }
              }
            } else if (firstTicket.timer_status === 'paused') {
              // OCT28 WP-2b-2b-ii-b2-A: the browser no longer measures pause ("wall clock − worked" counted parked
              // hours, nights and breaks). The server reconcile (pg_cron → run-long-autobump) derives the paused
              // figure every minute; this fallback asks the server only while ITS reconcile is not fresh (pending,
              // stale, failing or unknown), and the server derives and answers (UNCHANGED when nothing is due).
              if (!isScheduleFresh(getScheduleFreshness(shopId))) {
                
                if (isRolloverTicket && parentTicket) {
                  // THIS TICKET IS THE ROLLOVER - extend THIS ticket's optifinish
                  // The parent ticket (yesterday) should stay locked at its EOD
                  // CSI-I2-A1 → Core-100 R4 (CORE-AC): the paused rollover-descendant self-extend is
                  // the server's 'pause_extend' derivation (finish = now + the work that remains;
                  // accumulated_bump_minutes = the pause). Mirrors the no-rollover paused branch below.
                  const mutateResult = await requestServerFinish(firstTicket.id, 'pause_extend', {
                    reason: 'auto_bump_rollover_paused_self_extend',
                    params: { when: 'pause_grew' },
                  });
                  if (mutateResult.refused) continue;
                  const updateError = mutateResult.success ? null : { message: mutateResult.error };

                  if (!updateError) {
                    console.log(`[AutoBump] Rollover ticket ${firstTicket.id} paused: extending opti_finish (parent: ${parentTicket.id})`);

                    await triggerBoardCascade(shopId!, todayStr, techId);

                    onTicketUpdate?.();
                  } else {
                    // FIX-R132-HIGH-1
                    reportAutoBumpFailure({ phase: 'rollover_paused', ticketId: firstTicket.id, shopId, err: updateError });
                  }
                } else if (hasRollover && rolloverTicket) {
                  // HAS ROLLOVER: Extend rollover ticket's predicted_minutes AND recalc its opti_finish
                  // Lock today's ticket at tech's shift end (strict EOD)

                  // M-1: Use scheduled_date for rollover day — UTC split is wrong for shops west of UTC
                  const rolloverDateStr = (rolloverTicket as any).scheduled_date
                    ?? (rolloverTicket.start_time
                      ? getShopDayLabel(rolloverTicket.start_time, shopTimezone)
                      : todayStr);
                  
                  // Paused past the day: the minutes it cannot do today are more
                  // future work for its chain (paused-side mirror of the running branch above).
                  // oct20d (item A): the server's ledger grows it — claimed by a compare-and-swap of
                  // accumulated_bump_minutes, exactly once. This fallback never grows the chain nor
                  // moves that watermark.
                  const rolloverUpdateError: any = null;
                  if ((firstTicket as any).rollover_conversion_id) {
                    // W4: approved conversion — the server allocation sizes the continuation (idempotent re-derivation).
                    void requestConversionMaintenance((firstTicket as any).rollover_conversion_id);
                  }

                  // CSI-I2-A1 → Core-100 R4 (CORE-AC): paused-side today's-ticket EOD-lock is the
                  // server's 'eod_lock' derivation. oct20d: no track — accumulated_bump_minutes is the
                  // growth ledger's watermark.
                  const mutateResult = await requestServerFinish(firstTicket.id, 'eod_lock', {
                    reason: 'auto_bump_paused_eod_lock_with_rollover',
                  });
                  if (mutateResult.refused || mutateResult.outcome === 'UNCHANGED') continue;
                  const updateError = mutateResult.success ? null : { message: mutateResult.error };

                  if (!updateError && !rolloverUpdateError) {
                    await triggerBoardCascade(shopId!, todayStr, techId);

                    console.log(`[AutoBump] Paused ticket ${firstTicket.id} locked at EOD ${tech.shift_end}; its paused minutes for rollover ${rolloverTicket.id} (${rolloverDateStr}) are left to the server growth ledger`);
                    onTicketUpdate?.();
                  } else {
                    // FIX-R132-HIGH-1
                    reportAutoBumpFailure({
                      phase: 'paused',
                      ticketId: firstTicket.id,
                      shopId,
                      err: updateError ?? rolloverUpdateError,
                    });
                  }
                } else {
                  // NO ROLLOVER: Normal behavior - extend today's ticket
                  // M-3: Use shared constant instead of inline const DEBUG = true
                  if (DEBUG_AUTO_BUMP) console.log(`[AutoBump] Paused ticket ${firstTicket.id}: asking the server (its reconcile is not fresh)`);

                  // V-A-6 → Core-100 R4 (CORE-AC): the server's 'pause_extend' derivation writes the
                  // finish (now + the work that remains) with accumulated_bump_minutes. The bespoke
                  // triggerBoardCascade below still follows.
                  const mutateResult = await requestServerFinish(firstTicket.id, 'pause_extend', {
                    reason: 'auto_bump_paused_norollover',
                    params: { when: 'pause_grew' },
                  });
                  if (mutateResult.refused) continue;
                  const updateError = mutateResult.success ? null : { message: mutateResult.error };

                  if (!updateError) {
                    await triggerBoardCascade(shopId!, todayStr, techId);

                    // FIX-R31-L-6: log ticket id instead of customer_name —
                    // don't emit PII into browser devtools / shop consoles.
                    console.log(`[AutoBump] Paused ticket ${firstTicket.id}: the server's pause_extend applied`);
                    onTicketUpdate?.();
                  } else {
                    // FIX-R132-HIGH-1
                    reportAutoBumpFailure({ phase: 'paused', ticketId: firstTicket.id, shopId, err: updateError });
                  }
                }
              }
            }

            // [AutoBump-DEBUG]
            console.log('[AutoBump-DEBUG] skip', { ticketId: firstTicket.id, reason: 'timer-running-or-paused' });
            continue; // Don't bump start_time for running/paused timers
          }

          // Rule: If timer is running (actually_started), ticket is locked - skip
          if (firstTicket.actually_started) {
            // Clear warning flag if timer started
            timerNotStartedWarningRef.current.delete(firstTicket.id);
            
            // Check for present vehicle priority bumping
            const presentVehiclePriority = shopCulture.present_vehicle_priority || 0;
            
            if (presentVehiclePriority > 0 && sortedTickets.length > 1) {
              const optiFinish = parseISO(firstTicket.opti_finish);
              const minutesUntilFinish = differenceInMinutes(optiFinish, now);
              
              // Within threshold? Check next ticket
              if (minutesUntilFinish <= presentVehiclePriority && minutesUntilFinish > 0) {
                const nextTicket = sortedTickets[1];
                
                // Is next ticket not present?
                if (!(nextTicket.parking_spot_id || nextTicket.bay_id || nextTicket.timer_status === 'running' || nextTicket.timer_status === 'paused')) {
                  // Find first present ticket after the non-present one
                  const firstPresentIndex = sortedTickets.findIndex(
                    (t, idx) => idx > 1 && !!(t.parking_spot_id || t.bay_id || t.timer_status === 'running' || t.timer_status === 'paused')
                  );
                  
                  if (firstPresentIndex !== -1) {
                    const presentTicket = sortedTickets[firstPresentIndex];
                    
                    // Calculate new start time for present ticket (1 min after current optiFinish)
                    const newPresentStart = addMinutes(optiFinish, 1);
                    
                    // Calculate when the bumped ticket would finish
                    const presentDuration = differenceInMinutes(
                      parseISO(presentTicket.opti_finish),
                      parseISO(presentTicket.start_time)
                    );
                    const newPresentFinish = addMinutes(newPresentStart, presentDuration);
                    
                    // Check if any scheduled tickets would be overlapped
                    const wouldOverlapScheduled = sortedTickets.some((t, idx) => {
                      if (idx <= firstPresentIndex || !t.scheduled_start_time) return false;
                      const scheduledStart = parseISO(t.scheduled_start_time);
                      return newPresentFinish > scheduledStart;
                    });
                    
                    // Core-100 R4 CORE-BO (OD-24 / owner decision 8: "suggestion"): a vehicle on site that could
                    // pull in is RECORDED as a capacity opening (cause parking_ready_for_pull_in) for a person to
                    // accept in Open capacity — where the full pull-forward planner checks Due Out, waiter promise,
                    // bay and technician. AutoBump used to move it itself (reassign-ticket, manualOverride:false,
                    // no human decision) and toasted success even when the move was refused. Nothing moves here.
                    if (!wouldOverlapScheduled) {
                      const { data: suggestion, error: suggestionError } = await supabase.rpc(
                        'record_vehicle_ready_opening' as never,
                        { p_ticket_id: presentTicket.id, p_window_start: newPresentStart.toISOString() } as never,
                      );
                      if (suggestionError) {
                        console.warn('[AutoBump] Present-vehicle suggestion not recorded:', suggestionError.message);
                      } else if (suggestion && !presentVehicleSuggestedRef.current.has(presentTicket.id)) {
                        presentVehicleSuggestedRef.current.add(presentTicket.id);
                        toast({
                          title: 'Vehicle ready to pull in',
                          description: `${presentTicket.customer_name || 'A ticket'} is on site and could move up — review it in Open capacity. Nothing was moved.`,
                          duration: 6000,
                        });
                      }
                    }
                  }
                }
              }
            }

            // [AutoBump-DEBUG]
            console.log('[AutoBump-DEBUG] skip', { ticketId: firstTicket.id, reason: 'actually-started' });
            continue;
          }

          // Check Waiter time flexibility for scheduled Waiters
          if (isScheduled) {
            // Prefer scheduled_start_time, but fall back to start_time when they differ significantly
            const scheduledISO = firstTicket.scheduled_start_time || firstTicket.start_time;
            if (!scheduledISO) {
              continue;
            }
            let scheduledStartUTC = parseISO(scheduledISO);
            if (firstTicket.start_time) {
              const startUTC = parseISO(firstTicket.start_time);
              const delta = Math.abs(differenceInMinutes(startUTC, scheduledStartUTC));
              // If fields are out of sync (e.g., legacy bad save), trust start_time for appointment
              if (delta >= 30) scheduledStartUTC = startUTC;
            }

            const waiterFlexMin = shopCulture.waiter_time_flex_min || 0;
            const bumpAmountMin = differenceInMinutes(now, scheduledStartUTC);
            
            // M-3: Use shared constant instead of inline const DEBUG = true
            if (bumpAmountMin > 0) {
              if (bumpAmountMin <= waiterFlexMin) {
                // Within flex threshold - allow bump (proceed to normal bump logic)
                if (DEBUG_AUTO_BUMP) console.log(`[AutoBump] Waiter ${firstTicket.id} late by ${bumpAmountMin}min (within ${waiterFlexMin}min flex)`);
              } else {
                // Exceeds threshold - trigger warning modal
                if (DEBUG_AUTO_BUMP) console.log(`[AutoBump] Waiter ${firstTicket.id} late by ${bumpAmountMin}min (exceeds ${waiterFlexMin}min flex) - trigger modal`);
                
                // Convert UTC scheduled time to shop timezone for display
                const scheduledTimeInShopTZ = toZonedTime(scheduledStartUTC, shopTimezone);
                
                window.dispatchEvent(new CustomEvent('waiter-bump-warning', {
                  detail: {
                    ticketId: firstTicket.id,
                    customerName: firstTicket.customer_name,
                    scheduledTime: scheduledTimeInShopTZ,
                    bumpAmount: bumpAmountMin,
                    flexThreshold: waiterFlexMin,
                    techId: techId,
                    techName: tech.name,
                  }
                }));
                // [AutoBump-DEBUG]
                console.log('[AutoBump-DEBUG] skip', { ticketId: firstTicket.id, reason: 'waiter-exceeds-flex' });
                continue;
              }
            } else {
              // Not late yet, skip
              if (DEBUG_AUTO_BUMP) console.log(`[AutoBump] Waiter ${firstTicket.id} not late yet, skip`);
              // [AutoBump-DEBUG]
              console.log('[AutoBump-DEBUG] skip', { ticketId: firstTicket.id, reason: 'waiter-not-late' });
              continue;
            }
          }

          // Rule: First unlocked ticket with no running timer bumps to current time
          if (isBefore(startTime, now)) {

            // [AutoBump-DEBUG] === DIAGNOSTIC ===
            console.log('[AutoBump-DEBUG] active-not-started bump-eligible', {
              ticketId: firstTicket.id,
              techId,
              startTime: firstTicket.start_time,
              now: now.toISOString(),
              predicted_minutes: firstTicket.predicted_minutes,
            });
            // [AutoBump-DEBUG] === END DIAGNOSTIC ===

            // Calculate new start time (ceil to next minute for stability)
            let newStartTime = ceilToNextMinute(now);

            // Check if new start time falls in a break
            const techBreaks = [
              ...(tech.recurring_breaks as any[] || []),
              ...(tech.non_recurring_breaks as any[] || []).filter((b: any) => b.date === todayStr)
            ];

            for (const brk of techBreaks) {
              // H-2: Use fromZonedTime so break windows are interpreted in shop TZ, not viewer machine TZ.
              // parseISO('2025-03-09T10:00') without offset is treated as LOCAL browser time —
              // a NY manager viewing an LA shop gets windows shifted by 3h.
              const breakStart = fromZonedTime(`${todayStr} ${brk.start}`, shopTimezone);
              const breakEnd = fromZonedTime(`${todayStr} ${brk.end}`, shopTimezone);
              
              if (isAfter(newStartTime, breakStart) && isBefore(newStartTime, breakEnd)) {
                // Jump to after break and align to minute
                newStartTime = ceilToNextMinute(breakEnd);
              }
            }

            // Calculate optifinish time using the CANONICAL break-aware
            // helper. Pre-fix, AutoBump computed
            //   optiFinish = addMinutes(newStartTime, optiTime)
            // which ignored breaks falling within the work window, while
            // cascade's locked-anchor branch uses
            //   calculateTicketOptiFinish(startMin, optiTime, breaksInMin)
            // (break-aware). The divergence meant AutoBump's atomic write
            // could land an opti_finish that disagreed with cascade's
            // cursor advancement, breaking the canonical +1 chain rule
            // for downstream tickets. By using the same helper here,
            // AutoBump's stored opti_finish matches what cascade computes
            // for the anchor, and the cursor advances correctly without
            // re-placing the anchor (see the `anchorTicketIds` option
            // passed to triggerBoardCascade below).
            const efficiency = tech.efficiency_percentage || 100;
            const buffer = resolvePlanningBufferPct(tech.buffer_percentage);
            const optiTime = calculateWallMinutes(firstTicket.predicted_minutes, efficiency, buffer);

            const newStartMinInShopTz = getMinutesInShopTZ(newStartTime.toISOString(), shopTimezone);
            const breaksInMin = techBreaks.map((b: any) => ({
              start: parseTimeToMinutes(b.start),
              end: parseTimeToMinutes(b.end),
            }));
            const optiFinishMinInShopTz = calculateTicketOptiFinish(
              newStartMinInShopTz,
              optiTime,
              breaksInMin,
            );
            // Reverse-convert via wall-minutes delta on the UTC start.
            // Equivalent to makeUTCFromShopMinutes(scheduledDate, optiFinishMin, tz)
            // but avoids depending on the server's makeUTCFromShopMinutes
            // helper from this client-side hook.
            const effectiveDurationMin = optiFinishMinInShopTz - newStartMinInShopTz;
            const optiFinish = addMinutes(newStartTime, effectiveDurationMin);

            // Check if optifinish would overlap with next scheduled ticket
            if (sortedTickets.length > 1) {
              const nextTicket = sortedTickets[1];
              if (nextTicket.type === 'Waiter') {
                const nextScheduledStart = parseISO(nextTicket.start_time);
                if (isAfter(optiFinish, nextScheduledStart)) {
                  // Cannot bump - would overlap with scheduled ticket
                  // [AutoBump-DEBUG]
                  console.log('[AutoBump-DEBUG] skip', { ticketId: firstTicket.id, reason: 'next-waiter-overlap' });
                  continue;
                }
              }
            }

            // Skip EOD modal for tickets that are already part of a rollover chain
            // `original_ticket_id` is intentionally NOT in the SELECT
            // (PostgREST 42703 — column missing from deployed schema).
            // Guard the read so an undefined property does not collapse
            // to "always truthy" via the historic `!== null` comparison.
            const originalTicketId =
              Object.prototype.hasOwnProperty.call(firstTicket, 'original_ticket_id')
                ? (firstTicket as any).original_ticket_id
                : null;

            const isRolloverChain =
              firstTicket.parent_ticket_id !== null ||
              firstTicket.rollover_of_ticket_id !== null ||
              originalTicketId !== null;

            // ROLLOVER CHAIN (canonical identity, not minutes): an unstarted
            // row whose future belongs to the chain planner — a continuation,
            // or a root that has one. Bumping it must never leave it running
            // past its dated end of day; the chain authority re-plans it (and
            // everything unresolved after it) from "now" instead — spilling
            // onto the next valid days, growing a day when it must.
            const chainGoverned = await isChainGovernedRow(firstTicket.id);

            if (!isRolloverChain && !chainGoverned) {
              // Check for EOD rollover with willingness buffer (per-day hours)
              const techHoursBump = { ...tech, hours: tech.hours as Record<string, { open: string; close: string }> | null };
              const dayHoursBump = getTechDayHours(techHoursBump, todayStr);
              const shiftEnd = getTechShiftBoundaryUtc(todayStr, dayHoursBump?.close || tech.shift_end, shopTimezone);
              const maxAllowedEnd = addMinutes(shiftEnd, willingnessRollPastEOD);

              if (isAfter(optiFinish, maxAllowedEnd)) {
                const minutesPastShiftEnd = Math.ceil((optiFinish.getTime() - shiftEnd.getTime()) / 60000);

                // THE WARNING AND THE INVARIANT ARE BOTH OBLIGATIONS.
                //
                // This block used to end in `continue`, which meant that the
                // moment a ticket could no longer finish today, the canonical
                // bump below stopped running — permanently. Measured in
                // production on TKT-0277: `start_time` froze at 10:59 AM and
                // was still 10:59 AM at 6:45 PM, because from ~5:49 PM onward
                // every tick took this branch. Bucket 1 §1.1 and Bucket 2
                // §2.8.1 both list "a virgin ticket may not remain scheduled
                // in the past" as a Final Non-Negotiable; Bucket 3 §3.13.3
                // separately requires Core to warn and require a decision.
                // The old code paid for the second by abandoning the first.
                //
                // It now raises the conflict AND falls through to the bump.
                const conflictKind = classifyEodConflict(firstTicket);

                if (conflictKind === 'started_work_rollover') {
                  // Unreachable today: the `actually_started` gate earlier in
                  // this loop already `continue`d. Kept so that if a future
                  // edit lets a started ticket reach here, it gets the
                  // started-work rollover workflow it is entitled to rather
                  // than silently inheriting the virgin semantics below.
                  window.dispatchEvent(new CustomEvent(STARTED_WORK_EOD_EXCEEDED_EVENT, {
                    detail: {
                      ticketId: firstTicket.id,
                      customerName: firstTicket.customer_name,
                      techName: tech.name,
                      techEOD: tech.shift_end,
                      rolloverMinutes: minutesPastShiftEnd,
                      ticketType: firstTicket.type ?? null,
                    }
                  }));
                  // [AutoBump-DEBUG]
                  console.log('[AutoBump-DEBUG] skip', { ticketId: firstTicket.id, reason: 'eod-exceeded' });
                  continue;
                }

                // VIRGIN. A scheduling conflict, not a continuation. The
                // separate event name routes it to a surface that does not
                // contain "Convert to Rollover" at all — see
                // `virginEodSchedulingConflict.ts` for why the boundary is a
                // name rather than a flag.
                //
                // Raised ONCE per ticket per session. The operator should not
                // have a modal reopened on them every 60 seconds while they
                // think about it; the card-level indicator carries the state
                // in the meantime. This suppresses only the NOTICE — the bump
                // below is unconditional, so dismissing the modal can never
                // re-freeze Start Time.
                if (!virginEodWarnedRef.current.has(firstTicket.id)) {
                  virginEodWarnedRef.current.add(firstTicket.id);
                  const virginDetail: VirginEodSchedulingConflictDetail = {
                    ticketId: firstTicket.id,
                    customerName: firstTicket.customer_name,
                    techName: tech.name,
                    techEOD: tech.shift_end,
                    minutesPastShiftEnd,
                    ticketType: firstTicket.type ?? null,
                  };
                  window.dispatchEvent(new CustomEvent(VIRGIN_EOD_EXCEEDED_EVENT, {
                    detail: virginDetail,
                  }));
                }

                // [AutoBump-DEBUG]
                console.log('[AutoBump-DEBUG] virgin-eod-conflict (bump continues)', {
                  ticketId: firstTicket.id,
                  minutesPastShiftEnd,
                });
                // NO `continue` — fall through to the canonical anchor bump.
              }
            }

            // R3-A Option B: the notices above stay with the board; the bump is the server's.
            if (serverOwnsWrites) continue;

            // Only update if it's a significant change and not in cooldown
            if (!isSignificantChange(firstTicket.start_time, newStartTime)) {
              // [AutoBump-DEBUG]
              console.log('[AutoBump-DEBUG] skip', { ticketId: firstTicket.id, reason: 'not-significant-change' });
              continue;
            }
            const lastUpdate = ticketCooldownRef.current[firstTicket.id] || 0;
            if (nowTs - lastUpdate < 30000) {
              // [AutoBump-DEBUG]
              console.log('[AutoBump-DEBUG] skip', { ticketId: firstTicket.id, reason: 'cooldown' });
              continue; // 30s per-ticket cooldown
            }

            // W4: an APPROVED rollover is maintained by the server (allocation + checkpoint), never re-planned from
            // a browser tick. Ask at most once per 5 minutes; the server answers NO-OP when nothing changed.
            if ((firstTicket as any).rollover_conversion_id) {
              void requestConversionMaintenance((firstTicket as any).rollover_conversion_id);
              ticketCooldownRef.current[firstTicket.id] = nowTs;
              continue;
            }
            if (chainGoverned) {
              const replan = await replanRolloverChainFromRow(
                firstTicket.id,
                { date: todayStr, min: newStartMinInShopTz },
                { label: `autobump ${firstTicket.id}` },
              );
              ticketCooldownRef.current[firstTicket.id] = nowTs;
              console.log('[AutoBump-DEBUG] chain row re-planned by the chain authority', {
                ticketId: firstTicket.id, outcome: replan?.status ?? 'none',
              });
              if (replan?.status === 'applied') onTicketUpdate?.();
              continue;
            }

            // V-A-6 → Core-100 R4 (CORE-AC): the canonical bump is the SERVER's 'bump_to_now'.
            // It re-reads the anchor, moves its start to the next whole minute past any break
            // (the server's clock, not this device's), derives the finish from that start with
            // the one derivation, and writes start_time + opti_finish (+ original_start_time /
            // accumulated_bump_minutes, from the stored row) in ONE compare-and-swapped write.
            // `expectedStartTime` makes a ticket that moved since this tick decided STALE (it is
            // re-decided next tick); `notAfter` keeps the server's finish off the next Waiter's
            // promised start. Downstream paths below (split-cascade via
            // reassign-ticket, the anchored triggerBoardCascade) use the
            // server's PERSISTED start and finish.
            const nextLocked = sortedTickets.length > 1 && sortedTickets[1].type === 'Waiter' ? sortedTickets[1].start_time : undefined;
            const mutateResult = await requestServerFinish(firstTicket.id, 'bump_to_now', {
              reason: 'auto_bump_to_current_time',
              expectedStartTime: firstTicket.start_time,
              params: nextLocked ? { notAfter: nextLocked } : undefined,
            });
            if (mutateResult.refused) {
              console.log('[AutoBump-DEBUG] skip', { ticketId: firstTicket.id, reason: `server-${mutateResult.outcome}` });
              continue;
            }
            const error = mutateResult.success ? null : { message: mutateResult.error };
            const persistedStart = mutateResult.data?.startTime ? parseISO(mutateResult.data.startTime) : newStartTime;
            const persistedFinish = mutateResult.data?.optiFinish ? parseISO(mutateResult.data.optiFinish) : optiFinish;
            const appliedDelta = Math.round(differenceInMinutes(persistedStart, startTime));

            // [AutoBump-DEBUG] === DIAGNOSTIC ===
            if (error) {
              console.warn('[AutoBump-DEBUG] start_time mutate FAILED', {
                ticketId: firstTicket.id,
                techId,
                oldStartTime: firstTicket.start_time,
                newStartTime: newStartTime.toISOString(),
                error: mutateResult.error,
              });
            } else {
              console.log('[AutoBump-DEBUG] start_time bumped', {
                ticketId: firstTicket.id,
                techId,
                from: firstTicket.start_time,
                to: persistedStart.toISOString(),
                deltaMin: appliedDelta,
              });
            }
            // [AutoBump-DEBUG] === END DIAGNOSTIC ===

            if (!error) {
              ticketCooldownRef.current[firstTicket.id] = nowTs;
              
              // oct20d auditfix6 (AUDIT6 WM2, founder item A — the browser is never a growth writer): this fallback
              // used to add the bump to the rollover chain's LAST part's accumulated_bump_minutes with a direct
              // client write. That column is the pause-track growth watermark (runLongAutoBumpPlan AP) and the pause
              // the server recorded (liveTicketCoverage), and the server engine never makes this write (its plan has
              // no lastPart write), so the browser fallback no longer does either. The database keeps a signed-in
              // client's watermark write out regardless (20270211700000). The bump itself is the server's
              // bump_to_now above, which keeps the bumped row's own bump counter.

              // If this is part of a split ticket, cascade bump to later part if needed
              if (firstTicket.split_group_id && firstTicket.split_sequence) {
                const { data: laterParts } = await supabase
                  .from('tickets')
                  .select('id, start_time, assigned_tech_id')
                  .eq('split_group_id', firstTicket.split_group_id)
                  .gt('split_sequence', firstTicket.split_sequence)
                  .in('status', ['Active', 'Paused'])
                  .order('split_sequence', { ascending: true });

                if (laterParts && laterParts.length > 0) {
                  for (const laterPart of laterParts) {
                    const laterPartStartTime = parseISO(laterPart.start_time);
                    
                    // Check if our optiFinish pushes into the later part's start time
                    if (isAfter(persistedFinish, laterPartStartTime) ||
                        differenceInMinutes(persistedFinish, laterPartStartTime) >= -1) {

                      // Bump later part to start 1 minute after our (server-derived) finish
                      const newLaterStartTime = addMinutes(persistedFinish, 1);
                      
                      // Route through reassign-ticket for full bay/skill/shift gating
                      const { error: laterError } = await supabase.functions.invoke('reassign-ticket', {
                        body: {
                          ticketId: laterPart.id,
                          targetTechId: laterPart.assigned_tech_id,
                          scheduledDate: todayStr,
                          desiredStartTime: newLaterStartTime.toISOString(),
                          moveReason: 'auto-bump-split-cascade',
                          manualOverride: false,
                        },
                      });
                      if (laterError) {
                        console.error(`[AUTO-BUMP] Split sibling reassign failed for ${laterPart.id}:`, laterError);
                      } else {
                        console.log(`[AUTO-BUMP] Cascaded split bump via reassign-ticket: ${laterPart.id} → ${format(newLaterStartTime, 'HH:mm')}`);
                      }
                    }
                  }
                }
              }
              
              // Cascade the time change to all subsequent tickets.
              // `triggerBoardCascade` resolves with `{ ok, error? }` rather
              // than throwing on cascade-board failure; the prior try/catch
              // only caught throws (rare). Capture the resolved status so
              // a failed cascade-board RPC isn't silently swallowed:
              //   - clear THIS ticket's per-ticket cooldown so the next
              //     AutoBump tick (within 60s) can retry the cascade,
              //   - dispatch `ticket-updated` so the Dayboard fallback
              //     refetch fires the authoritative state regardless of
              //     whether realtime delivered the partial bump.
              // No toast — one failed cascade-board call shouldn't spam.
              // The cascade itself self-heals on subsequent ticks because
              // the bumped start_time + opti_finish are already persisted
              // atomically (the server's bump_to_now above); the
              // chain's +1-minute downstream order will reconcile on the
              // next successful cascade.
              //
              // ANCHOR PINNING: pass the just-bumped ticket as the cascade
              // anchor. globalCascade.ts promotes this ID into its locked
              // set so the walker advances the cursor to anchor.opti_finish
              // without re-placing the anchor itself. Downstream flexible
              // tickets chain from anchor.opti_finish + 1 — preserving the
              // canonical chain rule. The opti_finish written above uses
              // calculateTicketOptiFinish so it matches what cascade would
              // recompute for the anchor (the invariant the anchor pinning
              // depends on).
              let cascadeResult: { ok: boolean; error?: string } | null = null;
              try {
                cascadeResult = await triggerBoardCascade(shopId!, todayStr, techId, [firstTicket.id]);
              } catch (error) {
                console.error('Error cascading auto-bump:', error);
                cascadeResult = { ok: false, error: error instanceof Error ? error.message : 'Cascade threw' };
              }

              if (!cascadeResult?.ok) {
                console.warn('[AutoBump] cascade failed after bump — clearing cooldown to allow retry', {
                  ticketId: firstTicket.id,
                  techId,
                  cascadeError: cascadeResult?.error,
                });
                delete ticketCooldownRef.current[firstTicket.id];
                void notifyDayboardInvalidated({
                  reason: 'cascade',
                  shopId: shopId || '',
                  ticketId: firstTicket.id,
                  scheduledDate: todayStr,
                  metadata: { cascadeError: cascadeResult?.error ?? null },
                });
              }

              onTicketUpdate?.();
            }
          }
         } catch (techError) {
           console.error('[AutoBump] Technician pass failed:', techId, techError);
           continue;
         }
        }

      } catch (error) {
        console.error('Auto-bump error:', error);
        // [AutoBump-DEBUG]
        console.error('[AutoBump-DEBUG] outer catch', error);
      } finally {
        lastRunAtRef.current = clock.nowMs();
        processingRef.current = false;
      }
    };

    // Run immediately on mount (cold-load eagerness preserved)
    processAutoBump();

    // VIS-A1: stagger recurring tick. AutoBump anchors at offset 0 — its
    // setInterval fires at mount-relative phase 0 / 60 / 120s, while
    // useOptiFinishPushback fires at +20 / 80 / 140s and
    // useConvertedRolloverGrowth at +40 / 100 / 160s. Steady-state ticks
    // never co-occur even after a long hidden period, eliminating the
    // synchronized thundering-herd of shop_profile + tickets + cascade
    // chains the trio previously fired on every minute boundary AND on
    // every tab refocus.
    intervalRef.current = createStaggeredInterval(
      processAutoBump,
      60_000,
      VIS_A1_STAGGER.AUTO_BUMP,
    );

    // Tab-return kick-tick: the normal 60s interval is intentionally a
    // no-op while `document.hidden` is true (V-E-2 R151-MED-2). Without
    // this listener, a backgrounded tab returning to the foreground waits
    // up to 60s for the next scheduled tick — which on the demo dashboard
    // leaves past-time tickets visibly stale for that whole window.
    // Firing processAutoBump() once on visibility return collapses that
    // post-return gap to the next React-microtask boundary. The existing
    // `processingRef` re-entrancy guard and 15s `lastRunAtRef` throttle
    // protect against thundering-herd if visibility flaps; the
    // `document.hidden` guard inside processAutoBump remains intact for
    // the recurring interval's own ticks.
    const handleVisibilityChange = () => {
      if (typeof document !== 'undefined' && !document.hidden) {
        // [AutoBump-DEBUG]
        console.log('[AutoBump-DEBUG] visibilitychange → kick-tick');
        processAutoBump();
      }
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
      intervalRef.current?.();
      intervalRef.current = null;
    };
  // R5-M-2: shopId in deps so effect reruns when auth resolves
  }, [enabled, selectedDayLabel, shopId, shopTimezone, onTicketUpdate]);
};

// LOW-6: calculateOptiTime removed — was a local duplicate of calculateWallMinutes
// from canonicalTimeMath.ts (identical formula). All call-sites now use calculateWallMinutes.

function ceilToNextMinute(date: Date): Date {
  const d = new Date(date);
  if (d.getSeconds() === 0 && d.getMilliseconds() === 0) return d;
  d.setSeconds(0, 0);
  d.setMinutes(d.getMinutes() + 1);
  return d;
}

function isSignificantChange(oldISO: string, nextDate: Date, thresholdMs = 60000): boolean {
  const oldTs = parseISO(oldISO).getTime();
  const nextTs = nextDate.getTime();
  return Math.abs(oldTs - nextTs) >= thresholdMs;
}

// Auto-bump now cascades the time change to all subsequent tickets
// When the first ticket is bumped to current time, all following tickets are recalculated
